import React, { SetStateAction, useCallback, useEffect, useState } from "react";

import {
  Alert,
  Button,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from "@mui/material";

import {
  Action,
  ActionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { Group } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/groups_pb";

import { regionValuesArr } from "../../../api/index";
import { DEFAULT_REGION_CODE } from "../../../constants/index";
import { actionTypeToString } from "../../../constants/periscope-actions";
import {
  difference,
  discreteNot,
  intersection,
  union,
} from "../../../helpers/app-settings-helpers";
import { ActionByRegion, GroupByRegion } from "../../../types/permissions";
import permissionStyles from "../styles";
import {
  addPermissions,
  listActionsByGroup,
  listAllActions,
  removePermissions,
} from "api/endpoints/periscope-admin";
import { callOptionsHeaders } from "api/helpers";

interface TransferListProps {
  setErrorMessage: React.Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<SetStateAction<boolean>>;
  selectedGroup: Group;
  groupsByRegion: GroupByRegion;
}
interface ActionWithLabel {
  label: string;
  action: Action;
}

const TransferList: React.FC<TransferListProps> = ({
  setErrorMessage,
  isLoading,
  setIsLoading,
  selectedGroup,
  groupsByRegion,
}: TransferListProps) => {
  const classes = permissionStyles();
  const [openPermissionSnackBar, setOpenPermissionSnackBar] = useState(false);
  const [allActionsList, setAllActionsList] = useState<Action[]>([]);
  const [allGroupActions, setAllGroupActions] = useState<Action[]>([]);
  const [checked, setChecked] = useState<Action[]>([]);
  const [availableActions, setAvailableActions] = useState<Action[]>([]);
  const [assignedActions, setAssignedActions] = useState<Action[]>([]);
  const [actionsByRegion, setActionsByRegion] = useState<ActionByRegion>({
    us: {
      actionsList: [],
    },
    eu: {
      actionsList: [],
    },
    au: {
      actionsList: [],
    },
  });
  const defaultRegion =
    regionValuesArr.length === 1 ? regionValuesArr[0] : DEFAULT_REGION_CODE;

  const handlePermissionSnackBarClose = () => {
    setOpenPermissionSnackBar(false);
  };

  const getAllActionsReq = async () => {
    setIsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return listAllActions(callOptionsHeaders(regionCode))
          .then((actionsListResp) => {
            if (regionCode === defaultRegion) {
              setAllActionsList(actionsListResp);
            }
            const updatedActionsByRegion = { ...actionsByRegion };
            updatedActionsByRegion[regionCode].actionsList = [
              ...actionsListResp,
            ];
            setActionsByRegion(updatedActionsByRegion);
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      })
    );
    setIsLoading(false);
  };

  // This tells us which actions have already been assigned to a group
  const getActionsByGroup = () => {
    if (selectedGroup) {
      setIsLoading(true);
      listActionsByGroup(
        { groupId: selectedGroup.id },
        callOptionsHeaders(DEFAULT_REGION_CODE)
      )
        .then((actionsListResp) => {
          setAllGroupActions(actionsListResp);
        })
        .catch((error) => {
          setErrorMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  // eslint-disable-next-line
  const getAllActionsList = useCallback(getAllActionsReq, []);
  // Listen for change in selected Group
  // eslint-disable-next-line
  const getGroupActions = useCallback(getActionsByGroup, [selectedGroup]);

  useEffect(() => {
    getAllActionsList();
  }, [getAllActionsList]);

  useEffect(() => {
    getGroupActions();
  }, [getGroupActions]);

  // Set the Assigned Actions, and set available actions from the difference between all actions
  // and assigned actions
  useEffect(() => {
    setAssignedActions(allGroupActions);
    setAvailableActions(difference(allActionsList, allGroupActions));
  }, [allGroupActions, allActionsList, selectedGroup]);

  const handleToggle = (action: Action) => () => {
    const newChecked = [...checked];
    const currentIndex = checked.indexOf(action);
    // if its not in the checked array add it to the checked array
    if (currentIndex === -1) {
      newChecked.push(action);
      // if its already in the checked array, at currentIndex position,
      // remove 1 item from the checked array
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const availableActionsChecked = intersection(checked, availableActions);
  const assignedActionsChecked = intersection(checked, assignedActions);

  const numberOfChecked = (actions: Action[]) => {
    return intersection(checked, actions).length;
  };

  const handleToggleAll = (actions: Action[]) => {
    if (numberOfChecked(actions) === actions.length) {
      setChecked(discreteNot(checked, actions));
    } else {
      setChecked(union(checked, actions));
    }
  };

  const getGroupIdByRegion = (regionCode: string) => {
    if (selectedGroup) {
      const targetRegionGroupsList = groupsByRegion[regionCode].groupsList;
      const targetGroup = targetRegionGroupsList.find(
        (group) => group.name === selectedGroup.name
      );

      if (targetGroup) {
        return targetGroup.id;
      }

      return selectedGroup.id;
    }

    return "";
  };

  // Gets the equivalent AU, EU action lists from the checked actions
  // needed to add and remove permissions across all of the region DBs
  const getMatchingActionListByRegion = (
    regionCode: string,
    actionsList: Action[]
  ) => {
    const targetRegionActionsList = actionsByRegion[regionCode].actionsList;
    // We will construct an equivalent array of the parameter actionsList for that particular region
    const targetRegionActionsListMatchingArray = [] as Action[];
    for (const actionsListItem of actionsList) {
      const targetAction = targetRegionActionsList.find(
        (action) => action.actionType === actionsListItem.actionType
      );

      if (targetAction) {
        targetRegionActionsListMatchingArray.push(targetAction);
      }
    }

    return targetRegionActionsListMatchingArray;
  };

  const addPermissionsReq = async (actions: Action[]) => {
    setIsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return addPermissions(
          {
            groupId: getGroupIdByRegion(regionCode),
            actionsList: getMatchingActionListByRegion(regionCode, actions),
          },
          callOptionsHeaders(regionCode)
        )
          .then(() => {
            if (regionCode === defaultRegion) {
              setOpenPermissionSnackBar(true);
            }
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      })
    );
    getGroupActions();
    setChecked([]);
    setIsLoading(false);
  };

  const removePermissionsReq = async (actions: Action[]) => {
    setIsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return removePermissions(
          {
            groupId: getGroupIdByRegion(regionCode),
            actionsList: getMatchingActionListByRegion(regionCode, actions),
          },
          callOptionsHeaders(regionCode)
        )
          .then(() => {
            if (regionCode === defaultRegion) {
              setOpenPermissionSnackBar(true);
            }
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      })
    );
    // Refresh the state of the assigned and available lists
    getGroupActions();
    setChecked([]);
    setIsLoading(false);
  };

  const customList = (title: React.ReactNode, actions: Action[]) => {
    // Remove Action Unspecified option if it is in the list
    actions = actions.filter(
      (action) => action.actionType !== ActionType.UNSPECIFIED
    );

    const actionLabels: ActionWithLabel[] = actions
      .map((action) => {
        const label = actionTypeToString(action.actionType);
        return {
          action,
          label,
        };
      })
      .filter((action) => action.label != "Action not specified")
      .sort((actionA, actionB) => actionA.label.localeCompare(actionB.label));

    return (
      <Card sx={classes.card}>
        <CardHeader
          sx={classes.cardHeader}
          avatar={
            <Checkbox
              onClick={() => handleToggleAll(actions)}
              checked={
                numberOfChecked(actions) === actions.length &&
                actions.length !== 0
              }
              indeterminate={
                numberOfChecked(actions) !== actions.length &&
                numberOfChecked(actions) !== 0
              }
              disabled={actions.length === 0}
              inputProps={{ "aria-label": "all actions selected" }}
            />
          }
          title={title}
          titleTypographyProps={{ variant: "body1" }}
          subheader={`${numberOfChecked(actions)}/${actions.length} selected`}
        />
        <Divider />

        {isLoading ? (
          <CircularProgress
            color="primary"
            sx={classes.loadingSpinner}
            thickness={1}
            size={90}
          />
        ) : (
          <List sx={classes.list} dense component="div" role="list">
            {actionLabels.map((labelItem: ActionWithLabel, index) => {
              const labelId = `transfer-list-all-item-${labelItem.action.actionType}-label`;
              return (
                <ListItem
                  key={`transfer-list-all-item-${labelItem.action.actionType}-${index}`}
                >
                  <ListItemButton
                    role="listitem"
                    onClick={handleToggle(labelItem.action)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={checked.indexOf(labelItem.action) !== -1}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ "aria-labelledby": labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={labelItem.label} />
                  </ListItemButton>
                </ListItem>
              );
            })}
            <ListItem />
          </List>
        )}
      </Card>
    );
  };

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      alignItems="center"
      sx={classes.root}
    >
      <Grid item>{customList("Available Actions", availableActions)}</Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            variant="contained"
            size="large"
            color="primary"
            sx={classes.button}
            onClick={() => addPermissionsReq(availableActionsChecked)}
            disabled={availableActionsChecked.length === 0}
          >
            &gt;
          </Button>
          <Button
            variant="contained"
            size="large"
            color="primary"
            sx={classes.button}
            onClick={() => removePermissionsReq(assignedActionsChecked)}
            disabled={assignedActionsChecked.length === 0}
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item>{customList("Assigned Actions", assignedActions)}</Grid>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openPermissionSnackBar}
        onClose={handlePermissionSnackBarClose}
        autoHideDuration={3500}
      >
        <Alert severity="success" variant="filled">
          Permissions successfully updated
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default TransferList;
